import React from 'react';
import { MapPin, Clock, Phone, ExternalLink } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/sections/Footer';
import Seo from '../components/Seo';
import { locations, businessInfo } from '../mock';
import {
  getLocalBusinessJsonLd,
  getBreadcrumbJsonLd,
} from '../lib/structuredData';

const phoneHref = (raw) => `tel:+${String(raw || '').replace(/[^\d]/g, '')}`;

const Ubicaciones = () => {
  const jsonLd = [
    ...getLocalBusinessJsonLd(),
    getBreadcrumbJsonLd([
      { name: 'Inicio', path: '/' },
      { name: 'Ubicaciones', path: '/ubicaciones/' },
    ]),
  ];

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Seo
        title="Ubicaciones de Brazolovers en Quito | 2 locales"
        description="Visítanos en nuestros dos locales en Quito: Matriz (Sergio Játiva) y Sucursal (Av. Eloy Alfaro y Rusia). Horarios, direcciones y cómo llegar."
        path="/ubicaciones/"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="container mx-auto px-4 md:px-6 py-10 flex-1">
        <h1 className="text-3xl md:text-4xl font-bold text-chocolate mb-2">
          Nuestras ubicaciones en Quito
        </h1>
        <p className="text-chocolate-light mb-8 max-w-2xl">
          Brazolovers tiene <strong>dos locales físicos</strong> en Quito donde puedes
          comprar nuestros brazos gitanos artesanales, pasteles, galletas y más. También
          atendemos <strong>pedidos a domicilio todos los días</strong>: de lunes a viernes
          de 08:00 a 19:00, sábados de 08:00 a 18:00 y domingos de 09:00 a 13:00.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {locations.map((loc) => (
            <article
              key={loc.id}
              className="bg-white rounded-2xl shadow-sm p-6 flex flex-col gap-4"
            >
              <h2 className="text-2xl font-bold text-chocolate">
                {loc.seo?.branchName || `Brazolovers — ${loc.name}`}
              </h2>

              <p className="flex items-start gap-2 text-chocolate-light">
                <MapPin className="w-5 h-5 mt-0.5 shrink-0 text-caramel" />
                <span>{loc.address}</span>
              </p>

              <div className="flex items-start gap-2 text-chocolate-light">
                <Clock className="w-5 h-5 mt-0.5 shrink-0 text-caramel" />
                <ul>
                  <li>{loc.hours.weekdays}</li>
                  <li>{loc.hours.saturday}</li>
                  <li>{loc.hours.sunday}</li>
                  {loc.hours.closed && <li>{loc.hours.closed}</li>}
                </ul>
              </div>

              <p className="flex items-center gap-2 text-chocolate-light">
                <Phone className="w-5 h-5 shrink-0 text-caramel" />
                <a href={phoneHref(loc.phone)} className="hover:text-caramel">
                  +{String(loc.phone).replace(/[^\d]/g, '')}
                </a>
              </p>

              <div className="flex flex-wrap gap-3 mt-auto pt-2">
                <a
                  href={loc.googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 bg-chocolate text-white px-4 py-2 rounded-full text-sm hover:bg-chocolate-dark transition-colors"
                >
                  Cómo llegar <ExternalLink className="w-4 h-4" />
                </a>
                {loc.uberEatsUrl && (
                  <a
                    href={loc.uberEatsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 border border-caramel text-chocolate px-4 py-2 rounded-full text-sm hover:bg-caramel/10 transition-colors"
                  >
                    Pedir por Uber Eats <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>

              {loc.mapUrl && (
                <iframe
                  title={`Mapa ${loc.name}`}
                  src={loc.mapUrl}
                  className="w-full h-56 rounded-xl border-0 mt-2"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              )}
            </article>
          ))}
        </div>
      </main>

      <Footer businessInfo={businessInfo} />
    </div>
  );
};

export default Ubicaciones;
